import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions, selectedAdminEnvironment, getSelectedAdminEnvironmentKey, ADMIN_ENV_STORAGE_KEY } from "../config/firebase";
import { canAccessAdminPortal } from "../utils/adminRoles";
import { createSupportSearchRequest, readSupportSearchResults, SEARCH_KINDS, supportSearchEnvironment, supportSearchLink } from "./supportSearchResults";
import "./styles/CommandCenter.css";

const environmentScope = () => supportSearchEnvironment(auth, functions, selectedAdminEnvironment, getSelectedAdminEnvironmentKey());
const supportRole = token => {
	const role = token?.claims?.role;
	return typeof role === "string" && role === role.trim() && canAccessAdminPortal(role) ? role : null;
};
const formatTime = value => value === null ? "Not recorded" : new Date(value).toISOString();

function CommandSearch({ session }) {
	const [query, setQuery] = useState("");
	const [restaurantId, setRestaurantId] = useState("");
	const [applied, setApplied] = useState(null);
	const [result, setResult] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const generation = useRef(0);
	const alive = useRef(false);
	const confirmedRole = useRef(null);
	const { user, uid, scope, projectId } = session;
	const sameScope = () => auth === session.auth && functions === session.functions && auth.app === session.app &&
		selectedAdminEnvironment === session.environment && selectedAdminEnvironment.firebaseConfig === session.config &&
		auth.currentUser === user && user.uid === uid && environmentScope() === scope;

	useEffect(() => {
		alive.current = true;
		return () => { alive.current = false; generation.current += 1; };
	}, []);

	const runSearch = async event => {
		event.preventDefault();
		const ticket = ++generation.current;
		const active = () => alive.current && generation.current === ticket;
		const current = () => active() && sameScope();
		setResult(null); setError(""); setLoading(false); setApplied(null);
		let request;
		try { request = createSupportSearchRequest(projectId, query, restaurantId); }
		catch { setError("Enter a search term of 2 to 128 characters without slashes, and a valid optional restaurant ID."); return; }
		setApplied(request); setLoading(true);
		const checkRole = (token, expected = confirmedRole.current) => {
			const role = supportRole(token);
			if (!role || (expected !== null && role !== expected)) {
				confirmedRole.current = null;
				setQuery(""); setRestaurantId(""); setApplied(null);
				setError(role ? "Support role changed. Submit a new search." : "Scerv admin access is required.");
				return null;
			}
			return role;
		};
		try {
			if (!current()) return;
			const token = await getIdTokenResult(user, true);
			if (!current()) return;
			const role = checkRole(token);
			if (!role) return;
			const search = httpsCallable(session.functions, "searchScervAdminRecords");
			const response = await search(request);
			if (!current()) return;
			const latestToken = await getIdTokenResult(user, true);
			if (!current() || !checkRole(latestToken, role)) return;
			const page = readSupportSearchResults(response?.data, { ...request, actorUid: uid });
			confirmedRole.current = role;
			setResult(page);
		} catch {
			if (current()) setError("Search could not be confirmed. Submit again to retry.");
		} finally {
			if (active()) {
				if (!sameScope()) {
					setQuery(""); setRestaurantId(""); setApplied(null); setResult(null);
					setError("Account or environment changed. Reload before searching.");
				}
				setLoading(false);
			}
		}
	};
	const page = sameScope() ? result : null;
	return <main className="command-center-container">
		<header className="command-center-header"><h1>Scerv Command Center</h1><span>{projectId}</span></header>
		<form className="command-search" onSubmit={runSearch} aria-label="Support search">
			<label>Search term<input aria-label="Search term" type="search" value={query} maxLength={128}
				onChange={event => setQuery(event.target.value)} autoComplete="off" /></label>
			<label>Restaurant ID <span>(optional)</span><input aria-label="Restaurant ID" value={restaurantId} maxLength={128}
				onChange={event => setRestaurantId(event.target.value)} autoComplete="off" spellCheck={false} /></label>
			<button type="submit" disabled={loading} aria-label="Search records" title="Search records">&#8981;</button>
		</form>
		{applied && <dl className="command-applied" aria-label="Applied search scope">
			<div><dt>Query</dt><dd>{applied.query}</dd></div>
			<div><dt>Restaurant</dt><dd>{applied.restaurantId || "Not specified"}</dd></div>
		</dl>}
		{error && <p role="alert" className="command-error">{error}</p>}
		<p role="status" className="command-status">{loading ? "Searching..." : page ?
			`${page.results.length} ${page.results.length === 1 ? "match" : "matches"} | Bounded matches` : ""}</p>
		{page && <>
			<div className="command-result-summary">
				<p>{page.hasMore ? "More matches are available. Narrow your query or specify a restaurant ID." : "Coverage: bounded matches."}</p>
				<p>Observed <time dateTime={formatTime(page.observedAtMs)}>{formatTime(page.observedAtMs)}</time></p>
			</div>
			{page.results.length === 0 ? <p className="command-empty">No matching records in this bounded search.</p> :
				<div className="command-table-scroll" role="region" aria-label="Search results" tabIndex={0}>
					<table className="command-table">
						<thead><tr><th>Kind</th><th>Record</th><th>Restaurant</th><th>Recorded status</th><th>Updated</th><th>View</th></tr></thead>
						<tbody>{page.results.map(row => {
							const link = supportSearchLink(row, projectId);
							return <tr key={`${row.kind}:${row.id}`}>
								<td>{SEARCH_KINDS[row.kind]}</td>
								<td><strong>{row.title}</strong><code>{row.id}</code></td>
								<td><code>{row.restaurantId || "Not specified"}</code></td>
								<td>{row.status}</td><td>{formatTime(row.updatedAtMs)}</td>
								<td>{link && <Link to={link.to} onClick={event => { if (!sameScope()) event.preventDefault(); }}>{link.label}</Link>}</td>
							</tr>;
						})}</tbody>
					</table>
				</div>}
		</>}
	</main>;
}

export default function CommandCenter() {
	const [user, loading, error] = useAuthState(auth);
	const session = useRef({ revision: 0 });
	const [, refreshEnvironment] = useState(0);
	useEffect(() => {
		const changed = event => {
			if (event.key === ADMIN_ENV_STORAGE_KEY || event.key === null) refreshEnvironment(value => value + 1);
		};
		window.addEventListener("storage", changed);
		return () => window.removeEventListener("storage", changed);
	}, []);
	const scope = environmentScope();
	const next = { user, uid: user?.uid, auth, functions, app: auth.app, environment: selectedAdminEnvironment,
		config: selectedAdminEnvironment.firebaseConfig, scope, projectId: selectedAdminEnvironment.projectId };
	if (Object.keys(next).some(key => next[key] !== session.current[key])) {
		session.current = { ...next, revision: session.current.revision + 1 };
	}
	if (loading) return <p role="status">Checking support access...</p>;
	if (error || !user || auth.currentUser !== user) return <p role="alert">Sign in with an authorized support account.</p>;
	if (!scope) return <p role="alert">Search environment does not match the active Firebase project. Reload before continuing.</p>;
	return <CommandSearch key={session.current.revision} session={session.current} />;
}
